import React, { useState, useEffect, useCallback } from "react";
import { SiZoho } from "react-icons/si";
import { TbTarget, TbEye, TbEyeOff, TbLock, TbWorld, TbShieldCheck, TbPlugConnected } from "react-icons/tb";
import { api } from "../../../lib/api";
import { Button, Field, Alert } from "../../../components";

// Zoho CRM — the third destination for the Attack Surface lead magnet.
//
// The odd one out of the three panels, because Zoho has no API key — every connection is
// OAuth. Two ways in:
//
//   Connect with Zoho — the normal path. A popup to Zoho's consent screen, one click, done.
//                       Uses BusinessTSG's registered Zoho app, so the admin copies nothing.
//   Advanced          — the admin's own Self Client: client ID, secret and a short-lived
//                       grant code. For orgs whose Zoho admins will not approve a
//                       third-party app, and for servers where the platform app is not set up.
//
// Either way the result is the same stored refresh token, which does not expire. Nothing
// here needs re-entering after the first connect.
//
// The other Zoho-shaped decision visible here is `mode`: Leads, Contacts, or both. Zoho
// treats a lead and a contact as two stages of one person, so "both" puts the same human in
// two modules — the card for it says so, and tells reps how to convert without duplicating.

const MODES = [
  {
    id: "leads",
    name: "Leads module",
    blurb:
      "One record per capture in Leads, matched on email. How most Zoho teams handle unqualified inbound — the rep converts it to a contact and deal themselves.",
  },
  {
    id: "contacts_deals",
    name: "Contacts (+ optional Deals)",
    blurb:
      "Matches the GoHighLevel and HubSpot panels: a contact, plus a deal if you pick a stage below.",
  },
  {
    id: "both",
    name: "Leads and Contacts",
    blurb:
      "Writes a lead and a contact for every capture, plus a deal if you pick a stage. When a rep converts the lead, they should attach it to the existing contact — otherwise Zoho creates a second one.",
  },
];

// Modes that write a Contact, and so can carry a Deal.
const WRITES_CONTACT = new Set(["contacts_deals", "both"]);

const DEFAULT_SOURCE = "Pipsqueak Lead Magnet";

const ZohoPanel = ({ isAdmin }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const [busy, setBusy] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const [meta, setMeta] = useState({ regions: [], required_scopes: [] });
  const [connected, setConnected] = useState(false);
  const [secretHint, setSecretHint] = useState("");
  const [reconnecting, setReconnecting] = useState(false);
  const [authType, setAuthType] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const [stages, setStages] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [loadingStages, setLoadingStages] = useState(false);
  const [stageError, setStageError] = useState("");

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [form, setForm] = useState({
    enabled: false,
    region: "us",
    client_id: "",
    client_secret: "",
    grant_code: "",
    mode: "leads",
    pipeline: "",
    stage: "",
    report_field_name: "",
    lead_source: DEFAULT_SOURCE,
  });

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const load = useCallback(async () => {
    try {
      const [d, m] = await Promise.all([
        api.get("s2/api/zoho/config"),
        api.get("s2/api/zoho/metadata").catch(() => ({ regions: [], required_scopes: [] })),
      ]);
      setMeta(m || { regions: [], required_scopes: [] });
      if (d?.configured) {
        setConnected(!!d.connected);
        setAuthType(d.auth_type || null);
        setSecretHint(d.client_secret_hint || "");
        setReconnecting(false);
        setForm((f) => ({
          ...f,
          enabled: !!d.enabled,
          region: d.region || "us",
          client_id: d.client_id || "",
          client_secret: "",
          grant_code: "",
          mode: d.mode || "leads",
          pipeline: d.pipeline || "",
          stage: d.stage || "",
          report_field_name: d.report_field_name || "",
          lead_source: d.lead_source || DEFAULT_SOURCE,
        }));
      }
      setError("");
    } catch (e) {
      setError(e.message || "Could not load integration settings.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // The callback page posts its outcome back here and closes itself. Only messages from
  // this app's own origin, with our marker, are believed — anything else on the page could
  // otherwise claim a connection succeeded.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.source !== "pipsqueak-zoho-oauth") return;
      setConnecting(false);
      if (data.ok) {
        setError("");
        setSaved(data.message || "Zoho CRM connected.");
        // A fresh connection starts disabled server-side; turn the toggle on in the form
        // so the admin's next Save is what activates it.
        set({ enabled: true });
        load().then(() => set({ enabled: true }));
        setTimeout(() => setSaved(""), 5000);
      } else {
        setError(data.message || "Zoho CRM could not be connected.");
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [load]);

  const connectWithZoho = async () => {
    setError(""); setConnecting(true);
    // Open the window synchronously, inside the click. Opening it after the await would
    // be treated as an unprompted popup and blocked by every major browser.
    const popup = window.open("", "pipsqueak-zoho-oauth", "width=600,height=760");
    try {
      const d = await api.post("s2/api/zoho/oauth/start", {});
      if (popup && !popup.closed) {
        popup.location.href = d.url;
        // If the admin closes the popup without finishing, stop showing "Connecting…".
        const watch = setInterval(() => {
          if (popup.closed) { clearInterval(watch); setConnecting(false); }
        }, 700);
      } else {
        // Popup blocked: go the long way round in this tab. The callback page explains
        // the outcome and the admin navigates back.
        window.location.href = d.url;
      }
    } catch (e) {
      if (popup && !popup.closed) popup.close();
      setConnecting(false);
      setError(e.message || "Could not start the Zoho connection.");
    }
  };

  // What the credential fields contribute to a request. Once connected, nothing: the
  // server falls back to the stored refresh token. A grant code is only ever sent on the
  // way in, and is cleared the moment it has been spent.
  const credentialBody = () =>
    connected && !reconnecting
      ? {}
      : {
          region: form.region,
          client_id: form.client_id,
          client_secret: form.client_secret,
          grant_code: form.grant_code,
        };

  const canAct = connected && !reconnecting
    ? true
    : !!(form.client_id && form.client_secret && form.grant_code);

  const fetchStages = async () => {
    if (!canAct) { setStageError("Connect Zoho first."); return; }
    setLoadingStages(true); setStageError("");
    try {
      const d = await api.post("s2/api/zoho/stages", credentialBody());
      setStages(d?.stages || []);
      setPipelines(d?.pipelines || []);
      if (!d?.stages?.length) setStageError("No deal stages found in this Zoho org.");
    } catch (e) {
      setStageError(e.message || "Could not read deal stages from Zoho.");
    } finally { setLoadingStages(false); }
  };

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      await api.post("s2/api/zoho/test", credentialBody());
      setTestResult({ ok: true, message: "Zoho accepted the credentials." });
    } catch (e) {
      setTestResult({ ok: false, message: e.message || "Zoho rejected the credentials." });
    } finally { setTesting(false); }
  };

  const save = async () => {
    setBusy(true); setError(""); setSaved("");
    try {
      await api.post("s2/api/zoho/integrate", {
        ...credentialBody(),
        zoho_mode: form.mode,
        zoho_pipeline: form.pipeline || null,
        zoho_stage: form.stage || null,
        zoho_report_field_name: form.report_field_name || null,
        zoho_lead_source: form.lead_source,
        zoho_enabled: form.enabled,
      });
      setSaved("Zoho CRM settings saved.");
      await load();
      setTimeout(() => setSaved(""), 3000);
    } catch (e) {
      setError(e.message || "Could not save. Check the client details and generate a fresh grant code.");
    } finally { setBusy(false); }
  };

  if (loading) return <p className="v2-hint">Loading…</p>;

  const dealsMode = WRITES_CONTACT.has(form.mode);
  // Deals are optional — no stage simply means no deal. A pipeline with no stage is the one
  // setting that cannot mean anything, and the server refuses it too.
  const stageMissing = dealsMode && !!form.pipeline && !form.stage;
  const oauthAvailable = !!meta.oauth_available;
  // The Self Client form shows when the admin asked for it, or when there is no other way.
  const showCredentials = (!connected || reconnecting) && (showAdvanced || !oauthAvailable);

  return (
    <div className="v2-settings-form v2-ghl">
      <Alert>{error}</Alert>
      {saved && <Alert tone="success">{saved}</Alert>}

      {/* Connection ------------------------------------------------------ */}
      <section className="v2-integration">
        <div className="v2-integration-head">
          <span className="v2-ghl-icon v2-ghl-icon--red"><SiZoho size={18} /></span>
          <div>
            <div className="v2-setting-name">Zoho CRM Integration</div>
            <div className="v2-setting-desc">Send captured leads into your Zoho CRM org</div>
          </div>
          <label className="v2-ghl-toggle" style={{ marginLeft: "auto" }}>
            <input type="checkbox" checked={form.enabled} disabled={!isAdmin}
                   aria-label="Zoho CRM integration active"
                   onChange={(e) => set({ enabled: e.target.checked })} />
            <span>{form.enabled ? "ACTIVE" : "INACTIVE"}</span>
          </label>
        </div>

        <div className={`v2-ghl-status${connected && form.enabled ? " on" : ""}`}>
          <span className="v2-ghl-dot" />
          {!form.enabled
            ? "Inactive — leads will not be sent to Zoho"
            : connected
              ? "Connected — leads will be sent on capture"
              : "Not connected yet — connect your Zoho account below"}
        </div>

        {connected && !reconnecting && (
          <div className="v2-ghl-help">
            Connected to the <b>{meta.regions.find((r) => r.key === form.region)?.label || form.region}</b> data
            centre{" "}
            {authType === "platform"
              ? "with Connect with Zoho."
              : <>with your Self Client <code>{form.client_id || "—"}</code>
                  {secretHint === "unreadable"
                    ? " — but the stored secret cannot be read back. Reconnect to fix this."
                    : ` (secret ${secretHint || "••••••••"}).`}</>}
            <div style={{ marginTop: ".5rem" }}>
              <Button onClick={() => { setReconnecting(true); setShowAdvanced(!oauthAvailable); }} disabled={!isAdmin}>
                <TbPlugConnected size={14} /> Reconnect
              </Button>
            </div>
          </div>
        )}

        {(!connected || reconnecting) && oauthAvailable && (
          <div className="v2-zoho-connect">
            <Button variant="primary" onClick={connectWithZoho} disabled={!isAdmin || connecting}>
              <SiZoho size={15} /> {connecting ? "Waiting for Zoho…" : "Connect with Zoho"}
            </Button>
            <p className="v2-ghl-help">
              Opens Zoho in a popup. Sign in as a Zoho user who can create {form.mode === "both" ? "leads, contacts and deals" : form.mode === "contacts_deals" ? "contacts and deals" : "leads"},
              review the access requested, and click <b>Accept</b>. Your data centre is detected automatically.
            </p>
            <button type="button" className="v2-linklike" onClick={() => setShowAdvanced((v) => !v)}>
              {showAdvanced ? "Hide advanced option" : "Advanced: use your own Zoho Self Client instead"}
            </button>
            {reconnecting && (
              <button type="button" className="v2-linklike" onClick={() => { setReconnecting(false); setShowAdvanced(false); }}>
                Cancel reconnect
              </button>
            )}
          </div>
        )}

        {showCredentials && (
          <>
            <div className="v2-ghl-help">
              <b>In Zoho, before you start:</b>
              <ol>
                <li>Open <b>api-console.zoho.com</b> (or your region's console) and choose <b>Self Client</b></li>
                <li>Create it, then open the <b>Generate Code</b> tab</li>
                <li>
                  Paste this scope list:{" "}
                  <code>{(meta.required_scopes || []).join(",")}</code>
                </li>
                <li>Set a duration of <b>10 minutes</b>, pick your CRM portal, and generate</li>
                <li>Copy the code and paste it below <b>straight away</b> — it is single-use and expires fast</li>
              </ol>
            </div>

            <Field label={<><TbWorld size={13} /> Data Centre *</>}>
              <select className="v2-input" value={form.region} disabled={!isAdmin}
                      aria-label="Zoho data centre"
                      onChange={(e) => set({ region: e.target.value })}>
                {(meta.regions || []).map((r) => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
              <p className="v2-ghl-help">
                The region your Zoho account lives in — the domain you sign in on. A refresh
                token from one data centre is meaningless at another, so changing this later
                needs a new grant code.
              </p>
            </Field>

            <div className="v2-grid2">
              <Field label={<><TbLock size={13} /> Client ID *</>}>
                <input className="v2-input" value={form.client_id} disabled={!isAdmin}
                       aria-label="Client ID" placeholder="1000.XXXXXXXX"
                       onChange={(e) => set({ client_id: e.target.value })} />
              </Field>
              <Field label={<><TbLock size={13} /> Client Secret *</>}>
                <div className="v2-ghl-secret">
                  <input className="v2-input" type={showSecret ? "text" : "password"}
                         value={form.client_secret} disabled={!isAdmin}
                         aria-label="Client secret"
                         onChange={(e) => set({ client_secret: e.target.value })} />
                  <button type="button" className="v2-ghl-reveal"
                          aria-label={showSecret ? "Hide secret" : "Show secret"}
                          onClick={() => setShowSecret((v) => !v)}>
                    {showSecret ? <TbEyeOff size={16} /> : <TbEye size={16} />}
                  </button>
                </div>
              </Field>
            </div>

            <Field label="Grant Code *">
              <input className="v2-input" value={form.grant_code} disabled={!isAdmin}
                     aria-label="Grant code" placeholder="1000.xxxxxxxx.xxxxxxxx"
                     onChange={(e) => set({ grant_code: e.target.value })} />
              <p className="v2-ghl-help">
                Exchanged once for a long-lived refresh token and then discarded — you will not
                need it again. If saving fails, generate a fresh one: codes are single-use.
              </p>
            </Field>

            {reconnecting && !oauthAvailable && (
              <Button onClick={() => { setReconnecting(false); set({ client_secret: "", grant_code: "" }); }}>
                Cancel reconnect
              </Button>
            )}
          </>
        )}

        {connected && !reconnecting && (
          <div>
            <Button onClick={test} disabled={testing}>
              {testing ? "Testing…" : "Test connection"}
            </Button>
            {testResult && (
              <Alert tone={testResult.ok ? "success" : "warning"}>{testResult.message}</Alert>
            )}
          </div>
        )}
        {showCredentials && (
          <p className="v2-hint">
            Saving is what connects: the grant code is exchanged once, then discarded. Test
            becomes available after that.
          </p>
        )}
      </section>

      {/* Where leads land ------------------------------------------------ */}
      <section className="v2-integration">
        <div className="v2-integration-head">
          <span className="v2-ghl-icon v2-ghl-icon--violet"><TbTarget size={18} /></span>
          <div>
            <div className="v2-setting-name">Where leads land</div>
            <div className="v2-setting-desc">Which Zoho modules each captured lead is written to</div>
          </div>
        </div>

        <div className="v2-zoho-modes">
          {MODES.map((m) => (
            <label key={m.id} className={`v2-zoho-mode${form.mode === m.id ? " on" : ""}`}>
              <input type="radio" name="zoho-mode" value={m.id} checked={form.mode === m.id}
                     disabled={!isAdmin}
                     onChange={() => set({ mode: m.id })} />
              <div>
                <div className="v2-setting-name">{m.name}</div>
                <div className="v2-setting-desc">{m.blurb}</div>
              </div>
            </label>
          ))}
        </div>

        {dealsMode && (
          <>
            <div>
              <Button variant="primary" onClick={fetchStages}
                      disabled={loadingStages || !isAdmin || !canAct}>
                {loadingStages ? "Fetching…" : "Fetch Pipelines & Stages"}
              </Button>
            </div>

            {stageError && <Alert tone="warning">{stageError}</Alert>}

            <div className="v2-grid2">
              <Field label="Pipeline">
                <select className="v2-input" value={form.pipeline}
                        disabled={!isAdmin || pipelines.length === 0}
                        aria-label="Pipeline"
                        onChange={(e) => set({ pipeline: e.target.value })}>
                  <option value="">
                    {pipelines.length ? "Default pipeline" : "Not used in this org"}
                  </option>
                  {pipelines.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                <p className="v2-ghl-help">
                  Only orgs with more than one deal pipeline have this. Leave it as-is otherwise.
                </p>
              </Field>
              <Field label="Deal Stage (optional)"
                     error={stageMissing ? "Pick a stage for this pipeline, or set the pipeline back to default." : ""}>
                <select className="v2-input" value={form.stage}
                        disabled={!isAdmin || stages.length === 0}
                        aria-label="Deal stage"
                        onChange={(e) => set({ stage: e.target.value })}>
                  <option value="">{stages.length ? "No deal — contacts only" : "Fetch stages to add deals"}</option>
                  {stages.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>
            </div>

            {/* A stage saved in an earlier session has a value but no fetched list to name
                it from. Say so, rather than rendering an empty select that looks like the
                setting was lost. */}
            {form.stage && stages.length === 0 && (
              <p className="v2-hint">
                Stage <code>{form.stage}</code> is already saved. Fetch stages to change it.
              </p>
            )}

            <p className="v2-hint">
              Leave the stage empty to create contacts without deals. With a stage, Zoho also needs
              a closing date on every deal — one is set 30 days out for the rep to correct.
            </p>
          </>
        )}
      </section>

      {/* Field mapping --------------------------------------------------- */}
      <section className="v2-integration">
        <Field label="Report Link Field API Name (Optional)">
          <input className="v2-input" value={form.report_field_name} disabled={!isAdmin}
                 aria-label="Report link field API name"
                 placeholder="Report_Link"
                 onChange={(e) => set({ report_field_name: e.target.value })} />
          <div className="v2-ghl-help">
            <b>To save the report link on the record:</b>
            <ol>
              <li>Go to <b>Setup → Modules and Fields</b> and open {form.mode === "both" ? "Leads, then Contacts" : dealsMode ? "Contacts" : "Leads"}</li>
              <li>Add a <b>Single Line</b> (or URL) field</li>
              <li>Open its properties, copy the <b>API Name</b>, and paste it here</li>
            </ol>
            {form.mode === "both" && (
              <p style={{ margin: ".25rem 0" }}>
                <b>Both modules need a field with this exact API name.</b> If only one has it, Zoho
                rejects the record in the other module.
              </p>
            )}
            Leave blank to send no link. An API name that does not exist in your org makes Zoho
            reject the whole record, so this has no default.
          </div>
        </Field>

        <Field label="Lead Source">
          <input className="v2-input" value={form.lead_source} disabled={!isAdmin}
                 aria-label="Lead source"
                 onChange={(e) => set({ lead_source: e.target.value })} />
          <p className="v2-ghl-help">
            Written to the record's Lead Source field. Zoho only stores values that exist in that
            picklist, so add this one under Setup → Modules and Fields if it is not there.
          </p>
        </Field>
      </section>

      <div className="v2-ghl-note">
        <TbShieldCheck size={15} />
        <span>
          <b>How it works:</b> When a lead unlocks their report, their details are matched into
          Zoho on email — created if new, updated if they already exist. Zoho runs alongside
          GoHighLevel and HubSpot; enabling one does not turn the others off.
        </span>
      </div>

      <div>
        <Button variant="primary" onClick={save}
                disabled={busy || !isAdmin || stageMissing || (showCredentials ? !canAct : !connected)}>
          {busy ? "Saving…" : showCredentials ? "Connect Zoho CRM" : "Save Zoho settings"}
        </Button>
      </div>
    </div>
  );
};

export default ZohoPanel;
